import { useThemeContext } from '#contexts/ThemeContext';
import { LoadingDiv } from '#layouts/main-layout/LoadingDiv';
import { EAnswerItemType } from '#models/layout';
import { Answer, EQuestionType, QuestionnaireState, QuestionNode } from '#models/questionnaire';
import { HelperDialog } from '#modules/HelperDialog';
import { LargeMultipleAnswer } from '#modules/LargeMultipleAnswer';
import { MultipleAnswer } from '#modules/MultipleAnswer';
import { PictureAnswer } from '#modules/PictureAnswer';
import { useBreakpoints } from '#utils/breakpoint';
import { Box, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useField, useFormikContext } from 'formik';
import { sortBy } from 'lodash-es';
import React from 'react';

export interface QuestionLayoutProps {
  questionnaireId: string;
  currentQuestion: QuestionNode;
  handleSelectAnswer: (value: Answer) => void;
  questionnaireState: QuestionnaireState;
  setQuestionnaireStateMerge: (questionnaireState: any) => void | Promise<any>;
  trackMultiAnswerEvent: (answers: Answer[], question?: QuestionNode) => void;
}

export const QuestionLayout = ({
  questionnaireId,
  currentQuestion: { answers, type, multipleSelection },
  handleSelectAnswer,
  questionnaireState,
  setQuestionnaireStateMerge,
  trackMultiAnswerEvent
}: QuestionLayoutProps) => {
  const { handleSubmit, isSubmitting, status } = useFormikContext();
  const { primaryButtonBackground, primaryButtonText } = useThemeContext();
  const { isLessThanSm } = useBreakpoints();
  const [{ value: answerSelectionIds = [] as string[] }, , { setValue: setAnswerSelections }] =
    useField('answerSelectionIds');

  const onSelectAnswer = (answer: Answer) => {
    const _answerSelections = [...answerSelectionIds];
    if (!multipleSelection) {
      answers.forEach((a) => {
        const foundIndex = _answerSelections.findIndex((i) => i === a.id);
        if (foundIndex !== -1) {
          _answerSelections.splice(foundIndex, 1);
        }
      });
      _answerSelections.push(answer.id);
    } else {
      const foundIndex = _answerSelections.findIndex((i) => i === answer.id);
      if (foundIndex !== -1) {
        _answerSelections.splice(foundIndex, 1);
      } else _answerSelections.push(answer.id);
    }

    setAnswerSelections(_answerSelections);
    if (!multipleSelection) {
      handleSelectAnswer(answer);
      if (!answer.nextQuestionId) {
        handleSubmit();
      }
    }
    setTimeout(() =>
      setQuestionnaireStateMerge({
        ...questionnaireState,
        answerSelectionIds: _answerSelections
      })
    );
  };

  const handleNextAnswer = () => {
    trackMultiAnswerEvent(answers.filter((x) => answerSelectionIds.includes(x.id)));
    const answer = answers[0];
    if (answer.nextQuestionId) handleSelectAnswer(answer);
    else handleSubmit();
  };

  const [dialogState, setDialogState] = React.useState({
    open: false,
    helperText: ''
  });

  const showHelperText = React.useCallback(
    (text: string) => {
      setDialogState({
        open: true,
        helperText: text
      });
    },
    [setDialogState]
  );
  const { isXs, isSm } = useBreakpoints();
  const isShowAnswerAsRow = (isXs || isSm) && type === EQuestionType.Picture && answers?.length % 2;
  const isEvenAnswerLessThan3 = answers?.length % 2 === 0 && answers?.length < 3;
  const isEvenAnswerLessThan5 = answers?.length % 2 === 0 && answers?.length < 5;

  const responsiveItemProps: any = {
    [EQuestionType.Picture]: {
      xs: 12,
      sm: 12,
      md: answers?.length < 4 ? 12 / answers?.length : 3,
      lg: answers?.length < 4 ? 12 / answers?.length : 3,
      xl: answers?.length < 4 ? 12 / answers?.length : 3
    },
    [EQuestionType.Multiple]: {
      xs: 12,
      sm: 12,
      md: answers?.length < 4 ? 12 / answers?.length : 3,
      lg: answers?.length < 4 ? 12 / answers?.length : 3,
      xl: answers?.length < 4 ? 12 / answers?.length : 3
    }
  };
  const justifyStyle = makeStyles((theme: any) => {
    const lengthAnswer = answers?.length;
    return {
      getJustify: {
        [theme.breakpoints.up('xs')]: {
          justifyContent: !(lengthAnswer < 2) ? 'flex-start' : 'center'
        },
        [theme.breakpoints.up('sm')]: {
          justifyContent: !(lengthAnswer < 2) ? 'flex-start' : 'center'
        },
        [theme.breakpoints.up('md')]: {
          justifyContent: !(lengthAnswer < 3) ? 'flex-start' : 'center'
        },
        [theme.breakpoints.up('lg')]: {
          justifyContent: !(lengthAnswer < 3) ? 'flex-start' : 'center'
        },
        [theme.breakpoints.up('xl')]: {
          justifyContent: !(lengthAnswer < 4) ? 'flex-start' : 'center'
        }
      },
      answerWidth: {
        [theme.breakpoints.up('xs')]: {
          maxWidth: 370,
          width: 'calc(100% - 50px)'
        },
        [theme.breakpoints.up('sm')]: {
          maxWidth: 395,
          width: 'calc(100% - 50px)'
        },
        [theme.breakpoints.up('md')]: {
          maxWidth: 772,
          width: lengthAnswer > 4 ? 'calc(100% - 40px)' : 'auto'
        },
        [theme.breakpoints.up('lg')]: {
          maxWidth: 1112,
          width: lengthAnswer > 4 ? 'calc(100% - 48px)' : 'auto'
        },
        [theme.breakpoints.up('xl')]: {
          maxWidth: 1112,
          width: 'auto'
        }
      },
      nextButton: {
        width: 96,
        height: 96,
        borderRadius: '50%',
        background: primaryButtonBackground,
        color: primaryButtonText,
        '&:hover': {
          backgroundColor: primaryButtonBackground,
          opacity: 0.8
        },
        '&.Mui-disabled': {
          backgroundColor: primaryButtonBackground,
          opacity: 0.5,
          color: primaryButtonText
        },
        [theme.breakpoints.down('md')]: {
          width: 76,
          height: 76
        },
        [theme.breakpoints.down('sm')]: {
          width: 70,
          height: 70
        }
      }
    };
  });
  const useStyles = justifyStyle();
  return (
    <React.Fragment>
      <Box display='flex' mx='auto' flexDirection='column' pb={8}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {/* Handle for Image Answer */}
            {type === EQuestionType.Picture && (
              <Box margin='0 auto' className={useStyles.answerWidth}>
                <Grid container justifyContent='flex-start' alignItems='stretch' spacing={2}>
                  {sortBy(answers, 'sortOrder').map((answer: Answer) => (
                    <Grid item key={answer.id} {...responsiveItemProps[type]} onClick={() => onSelectAnswer(answer)}>
                      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        <PictureAnswer
                          isEvenAnswer={!!(answers?.length % 2)}
                          answer={answer}
                          type={isShowAnswerAsRow ? EAnswerItemType.Row : EAnswerItemType.Column}
                          showHelperText={showHelperText}
                          selected={answerSelectionIds.includes(answer.id)}
                          multipleSelection={multipleSelection}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Handle for Multiple Choice */}
            {type === EQuestionType.Multiple && (
              <Box margin='0 auto' className={useStyles.answerWidth}>
                <Grid container justifyContent='flex-start' alignItems='stretch' spacing={2}>
                  {sortBy(answers, 'sortOrder').map((answer: Answer) => (
                    <Grid item key={answer.id} {...responsiveItemProps[type]} onClick={() => onSelectAnswer(answer)}>
                      <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        {isLessThanSm ? (
                          <MultipleAnswer
                            answer={answer}
                            showHelperText={showHelperText}
                            selected={answerSelectionIds.includes(answer.id)}
                            multipleSelection={multipleSelection}
                          />
                        ) : (
                          <LargeMultipleAnswer
                            answer={answer}
                            showHelperText={showHelperText}
                            selected={answerSelectionIds.includes(answer.id)}
                            multipleSelection={multipleSelection}
                          />
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Grid>
        </Grid>
        {multipleSelection && (
          <Grid item xs={12}>
            <Box
              width={'100%'}
              height={'100%'}
              maxWidth={'100%'}
              mt={3}
              mx='auto'
              display='flex'
              justifyContent='flex-end'
            >
              <Button
                disabled={!answers.find((item) => answerSelectionIds.includes(item.id))}
                variant='outlined'
                fullWidth
                className={useStyles.nextButton}
                onClick={handleNextAnswer}
                size='large'
              >
                Next
              </Button>
            </Box>
          </Grid>
        )}
        <HelperDialog {...dialogState} setDialogState={setDialogState} />
      </Box>
      {(isSubmitting || status) && <LoadingDiv open={isSubmitting || status} />}
    </React.Fragment>
  );
};
